import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useQuery } from "@tanstack/react-query";
import { OrderStatus } from "types/order-status";

export interface Order {
  id: string;
  user_id: string;
  project_ids: string[];
  created_at: string;
  price: number;
  status: OrderStatus;
  stripe_session_id: string | null;
  id_number: string | null;
  product_ids: string[];
  delivery_address: Record<string, any> | null;
  user_email: string;
  user_full_name: string | null;
  total_count?: number;
}

interface RawOrder {
  id: string;
  user_id: string;
  project_ids: string;
  created_at: string;
  price: number;
  status: string;
  stripe_session_id: string | null;
  id_number: string | null;
  product_ids: string;
  delivery_address: Record<string, any> | null;
  user_email: string;
  user_full_name: string | null;
  total_count: number;
}

interface UseOrdersOptions {
  status?: string;
  daysBack?: number;
  limit?: number;
  offset?: number;
}

export const useOrders = (options: UseOrdersOptions = {}) => {
  const { client, session } = useAuthContext();
  const { status = "created", daysBack = 7, limit = 50, offset = 0 } = options;

  const getOrders = async (): Promise<{ orders: Order[]; totalCount: number }> => {
    if (!client) throw new Error("useOrders: Client not initialized");
    if (!session) throw new Error("useOrders: No active session");

    const { data, error } = await client.functions.invoke("admin-list-orders", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: { status, daysBack, limit, offset },
    });

    if (error) throw error;

    const rawOrders: RawOrder[] = data.orders || [];
    const orders = rawOrders.map((order) => ({
      id: order.id,
      user_id: order.user_id,
      project_ids: order.project_ids ? order.project_ids.split(",") : [],
      created_at: order.created_at,
      price: order.price,
      status: order.status as OrderStatus,
      stripe_session_id: order.stripe_session_id,
      id_number: order.id_number,
      product_ids: order.product_ids ? order.product_ids.split(",") : [],
      delivery_address: order.delivery_address,
      user_email: order.user_email,
      user_full_name: order.user_full_name,
      total_count: order.total_count,
    }));

    return {
      orders,
      totalCount: data.totalCount || 0,
    };
  };

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["admin-orders", status, daysBack, limit, offset],
    queryFn: getOrders,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    enabled: !!session,
  });

  return {
    orders: data?.orders,
    totalCount: data?.totalCount || 0,
    refetch,
    isLoading,
    isFetching,
    isError,
  };
};
